/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { debounce, pick } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';

import { OperationIdBadge } from 'src/app/graph/operationsPage/OperationIdBadge';
import { CardWithHeader } from 'src/app/graph/operationsPage/report-pane/section-report/performance-section/serviceReport/CardWithHeader';
import { SparklineChart } from 'src/components/charts/SparklineChart';
import { Metric } from 'src/components/report-metrics/metric/Metric';
import {
  TimeRangeOption,
  printTimeRangeLabel,
} from 'src/lib/config/timeRangeOptions';
import { format } from 'src/lib/format';
import { OperationType } from 'src/lib/graphqlTypes/types';
import { formatMetrics, metricConfig } from 'src/lib/metrics';
import {
  isOperationUnexecutable,
  unexecutableOperationReason,
} from 'src/lib/unexecutableOperationReason';

import { MutationBadge } from '../common/operationTypeBadge/MutationBadge';
import { SubscriptionBadge } from '../common/operationTypeBadge/SubscriptionBadge';
import { Tooltip } from '../common/tooltip/Tooltip';

// using _pick preserves the correct order of charts
export const OVERVIEW_METRICS = pick(metricConfig.allOperationsMetrics, [
  'requestRate',
  'serviceTimeP95',
  'errorPercentage',
]);
export const OVERVIEW_METRIC_KEYS = Object.keys(
  OVERVIEW_METRICS,
) as (keyof typeof OVERVIEW_METRICS)[];

export interface OperationOverviewMetadata {
  id: string;
  name: string;
  type: OperationType | null;
  requestRate: number | null;
  errorPercentage: number | null;
  serviceTimeP95: number | null;
}

export function OverviewCard({
  currentValue,
  pastValue,
  sortKey,
  timeRange,
  sparklineData,
  min,
  max,
  topOperations,
  onQuerySelect,
}: {
  currentValue: number;
  pastValue: number | null;
  sortKey: keyof typeof OVERVIEW_METRICS;
  timeRange: TimeRangeOption;
  sparklineData: number[];
  min: number;
  max: number;
  topOperations: OperationOverviewMetadata[] | undefined;
  onQuerySelect: (
    data: { id: string | null; name: string | null },
    metric: string,
  ) => void;
}) {
  const [chartWidth, setChartWidth] = useState(250);
  const selfRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    const onResize = debounce(() => {
      if (!selfRef.current) {
        return;
      }
      setChartWidth(selfRef.current.offsetWidth);
    }, 50);

    window.addEventListener('resize', onResize);
    onResize();
    onResize.flush();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const formattedMin = format.splitUnits(formatMetrics(sortKey, min));
  const formattedMax = format.splitUnits(formatMetrics(sortKey, max));
  const delta =
    // we can only compute the delta when we have a past value to compare to.
    pastValue !== null
      ? // if past value is 0, just show 100% delta.
        (100 * (currentValue - pastValue)) / (pastValue || currentValue || 1)
      : null;

  const renderCardTitle = () => {
    switch (sortKey) {
      case 'errorPercentage':
        return 'Error percentage';
      case 'requestRate':
        return 'Request rate';
      case 'serviceTimeP95':
        return 'p95 service time';
      default:
        return '';
    }
  };
  const renderQueryListTitle = () => {
    switch (sortKey) {
      case 'errorPercentage':
        return 'Highest Error Percentages';
      case 'requestRate':
        return 'Highest Request Rates';
      case 'serviceTimeP95':
        return 'Slowest p95 Service Times';
      default:
        return '';
    }
  };
  const renderQueryListSubtitle = () => {
    switch (sortKey) {
      case 'errorPercentage':
        return 'Operation requests causing the most errors.';
      case 'requestRate':
        return 'Operations with highest requests/min.';
      case 'serviceTimeP95':
        return 'Operations with slowest p95 service times.';
      default:
        return '';
    }
  };
  return (
    <CardWithHeader
      title={renderCardTitle()}
      subTitle={renderQueryListSubtitle()}
      className="w-full"
      cardContent={
        <div>
          <div
            className="flex items-center justify-between"
            css={css`
              svg {
                fill: none;
                stroke: ${sortKey === 'errorPercentage'
                  ? customColors.border.error
                  : sortKey === 'serviceTimeP95'
                    ? customColors.border.beta
                    : customColors.border.info};
              }
            `}
          >
            <div className="flex-none">
              <Metric
                {...format.splitUnits(formatMetrics(sortKey, currentValue))}
              />
              <div className="mt-1">{`Median for the ${printTimeRangeLabel(
                timeRange,
                {
                  custom: 'Custom Range',
                },
              )?.toLowerCase()}`}</div>
              <div>
                <span className="font-semibold">Range:</span>{' '}
                {formattedMin.value}
                {formattedMin.unit} - {formattedMax.value}
                {formattedMax.unit}
              </div>
              {delta !== null && (
                <div className="flex">
                  <span
                    className={classnames('mr-px', {
                      'text-green': delta >= 0,
                      'rotate-180 text-red': delta < 0,
                    })}
                  >
                    ▲
                  </span>
                  <Metric
                    className="mr-1"
                    nosize={true}
                    value={`${delta > 0 ? '+' : ''}${format.number(
                      delta,
                      '0.00',
                    )}`}
                    unit="%"
                  />
                  <span> since yesterday</span>
                </div>
              )}
            </div>
            <div ref={selfRef} className="ml-6 w-full flex-1 overflow-hidden">
              <SparklineChart
                width={chartWidth}
                height={60}
                margin={0}
                data={sparklineData}
              />
            </div>
          </div>

          <div className="mb-1 mt-4 text-sm font-semibold uppercase text-secondary">
            {renderQueryListTitle()}
          </div>

          {topOperations?.map((operation) => {
            const { id, name, type, [sortKey]: metric } = operation;

            return (
              <div
                className="group -mx-2 flex cursor-pointer items-center rounded px-2 py-1.5 hover:bg-btn-secondary-hover"
                key={`query-${id}`}
                onClick={() => onQuerySelect(operation, sortKey)}
                data-testid="overview-card-operation-row"
              >
                <div className="mr-1 flex min-w-0 flex-1 items-center ">
                  {isOperationUnexecutable(name) ? (
                    <>
                      <span
                        title={name}
                        className="truncate italic text-secondary hover:text-black"
                      >
                        {name}
                      </span>
                      <Tooltip label={unexecutableOperationReason(name)}>
                        <IconInfo className="ml-2 text-link" />
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <span title={name} className="truncate">
                        {name}
                      </span>
                      {id && (
                        <OperationIdBadge className="ml-1.5 mr-1" id={id} />
                      )}
                    </>
                  )}

                  {type === OperationType.MUTATION ? (
                    <MutationBadge />
                  ) : type === OperationType.SUBSCRIPTION ? (
                    <SubscriptionBadge />
                  ) : null}
                </div>
                <Metric
                  className="flex-none"
                  nosize={true}
                  {...format.splitUnits(formatMetrics(sortKey, metric))}
                />
              </div>
            );
          })}
        </div>
      }
    />
  );
}
